import { Component, Context, h, Fragment } from 'preact';
import { IntlContext, MarkupText } from 'preact-i18n';
import { ANIMATION_DURATION, Availability, Locations, Properties, WeeklyFlights } from '../../../common';
import { IntlContextWithDictionary, FinText } from '../../../i18n';
import { Row } from '../../../common/Layout/Layout';
import { DateFormat, dateFormat } from '../../../i18n/Format';
import { BookingState, connectStore, locationsSet } from '../../../store';
import { WithStyles, withStyles } from '../../../style';
import { isBlank, isNotEmpty, isPresent } from '../../../utils';
import Notification, { Severity } from '../Notification/Notification';
import * as appStyles from '../../../assets/global.scss';
import { asCmsLang } from '../../../utils/envUtils';

const TIMETABLE_ID = 'seasonalTimetable';

// == Utilities ================================================================

const sameYear = (date1: string, date2: string): boolean => {
  if (isBlank(date1) || isBlank(date2)) {
    return false;
  }
  return date2.startsWith(date1.substring(0, 4));
};

const localizeAvailability = ({ start, end }: Availability, context: IntlContextWithDictionary): Availability => {
  const startFormat: DateFormat = sameYear(start, end) ? DateFormat.MONTH : DateFormat.MONTH_AND_YEAR;
  return {
    start: dateFormat(start, startFormat, context),
    end: dateFormat(end, DateFormat.MONTH_AND_YEAR, context),
  };
};

interface StoreProps {
  open: boolean;
  locations: Locations;
  availability: Availability[];
  weeklyFlights: WeeklyFlights[];
  locale: string;
}

interface SeasonalNotificationState {
  showTimeTable: boolean;
}

type EffectiveProps = Properties & StoreProps & WithStyles;

const mapStateToProps = ({
  selection,
  price: { availability, weeklyFlights },
  ux: {
    warnings: { seasonalRoute },
  },
}: BookingState): StoreProps => {
  const open: boolean = seasonalRoute && isNotEmpty(availability) && locationsSet(selection);
  return {
    open,
    locations: selection.locations[selection.activeLocationIndex],
    availability,
    weeklyFlights,
    locale: selection.locale,
  };
};

@withStyles(appStyles)
@connectStore({ mapStateToProps })
export default class SeasonalNotification extends Component<Properties, SeasonalNotificationState> {
  static contextType: Context<IntlContextWithDictionary> = IntlContext;

  state: SeasonalNotificationState = {
    showTimeTable: false,
  };

  toggleTimeTable = (): void => {
    this.setState((prevState) => ({
      showTimeTable: !prevState.showTimeTable,
    }));
    if (isPresent(this.base) && this.state.showTimeTable) {
      const timeout = setTimeout(() => {
        const timetable: HTMLDivElement = (this.base as HTMLElement).querySelector(`#${TIMETABLE_ID}`);
        const { bottom } = timetable.getBoundingClientRect();
        // Only scroll timetable into view if its bottom is not visible
        if (bottom > window.innerHeight) {
          timetable.scrollIntoView(false);
        }
        clearTimeout(timeout);
      }, ANIMATION_DURATION / 2); // Match timetable transition duration and wait until it's finished before scrolling the page
    }
  };

  render(
    { open, availability, locations, locale, tx }: EffectiveProps,
    { showTimeTable }: SeasonalNotificationState
  ): h.JSX.Element {
    const fields: any = open
      ? availability.reduce(
          (acc: any, av: Availability, i: number) => {
            const postFix: string = i > 0 ? String(i + 1) : '';
            const { start, end } = localizeAvailability(av, this.context);
            acc[`start${postFix}`] = `<b>${start}</b>`;
            acc[`end${postFix}`] = `<b>${end}</b>`;
            return acc;
          },
          {
            origin: locations.origin.cityName,
            destination: locations.destination.cityName,
          }
        )
      : null;

    return (
      <Notification
        class={tx('font-body-1 flex--align-start')}
        open={open}
        severity={Severity.INFO}
        expanded={showTimeTable}
      >
        {open && (
          <Fragment>
            <Row vCenter={false} hCenter={false}>
              {/* == Primary text == */}
              <p class={tx('notificationTeaser', 'flex flex--column flex--center flex--align-start')}>
                <MarkupText id={'notification.route'} plural={availability.length} fields={fields} />
              </p>
            </Row>
            <Row vCenter={false} hCenter={false}>
              <a
                class={tx('underline medium-type link-hover nordic-blue-900-text font-body-1 ms-xsmall-y')}
                href={`/${asCmsLang(locale)}/timetables?dest=${locations.destination.locationCode}&origin=${
                  locations.origin.locationCode
                }`}
                rel="noreferrer"
              >
                <FinText id={`notification.timetable.forRouteLinkText`} />
              </a>
            </Row>
          </Fragment>
        )}
      </Notification>
    );
  }
}
